import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { Box, Button, Checkbox, FormControlLabel, Grid, Tooltip } from '@material-ui/core';
import { Link } from 'react-router-dom';
import FancyPlayerCountSelect from '../../../components/fancy/FancyPlayerCountSelect';
import FancyButtongroupMultiSelect from '../../../components/fancy/FancyButtongroupMultiSelect';
import { useIslands } from '../../../hooks/useIslands';
import { useIslandFilter } from '../../../hooks/useIslandFilter';
import FilterRangePicker from '../../../components/atom/FilterRangePicker';
import FilterDropdown from '../../../components/atom/FilterDropdown';
import FancySearchInputField from '../../../components/fancy/FancySearchInputField';
import { useSwaggerClient } from '../../../hooks/useSwaggerClient';
import { Alert } from '@material-ui/lab';
import moment from 'moment';
import { isTimeslotFilterLocked } from './urlFiltering';

const SORT_ALL_OPTIONS = Object.freeze([
    { value: 'ltoh', name: 'Price - Low to High' },
    { value: 'htol', name: 'Price - High to Low' },
    { value: 'atoz', name: 'Name - A to Z' },
    { value: 'ztoa', name: 'Name - Z to A' },
    { value: 'vtoz', name: 'Vendor - A to Z' },
    { value: 'vtoa', name: 'Vendor - Z to A' }
]);

const FILTER_AGENT_ORDER = [
    'hideShow',
    'asTable',
    'datePicker',
    'searchField',
    'sort',
    'guests',
    'island',
    'tags'
];
const FILTER_GUEST_ORDER = [
    'searchField',
    'sort',
    'asTable',
    'datePicker',
    'guests',
    'island',
    'tags',
    'hideShow'
];

const TimeRangeAlert = ({ checkInDate, checkOutDate, startDate, endDate, setFilters }) => {
    const [{ guestCheckIn, guestCheckOut }] = useState({
        guestCheckIn: checkInDate || startDate,
        guestCheckOut: checkOutDate || endDate
    });

    // We should compare the two date ranges and if they are different, show the alert
    let isDifferent = false;

    // let's consider that the checkinDate might be in the past, we cannot set that
    // so we only check if the dates are different
    const nowMoment = moment();

    if (checkInDate && startDate && !checkInDate.isBefore(nowMoment, 'day')) {
        if (!checkInDate.isSame(startDate, 'day')) {
            isDifferent = true;
        }
    }
    if (checkOutDate && endDate && !checkOutDate.isBefore(nowMoment, 'day')) {
        if (!checkOutDate.isSame(endDate, 'day')) {
            isDifferent = true;
        }
    }
    if (!isDifferent) {
        return null;
    }

    return (
        <Box
            sx={{
                width: '260px',
                marginBottom: '16px'
            }}
        >
            <Alert severity="info" icon={false}>
                Outside of travel dates!
            </Alert>
            <Button
                size="small"
                color="inherit"
                variant="contained"
                style={{ marginTop: '8px' }}
                fullWidth
                onClick={() => {
                    setFilters({
                        checkInDate: guestCheckIn,
                        checkOutDate: guestCheckOut
                    });
                }}
            >
                Set guest dates
            </Button>
        </Box>
    );
};

const ActivityFilter = ({
    filters,
    setFilters,
    isAgent,
    isAdmin,
    notAllFiltersSelected = false,
    vendorActivitySelected = false,
    guestTimeRange = null
}) => {
    const history = useHistory();
    const [islandOptions] = useIslands(false, isAdmin);
    const [tagOptions, tagOptionsLoading] = useSwaggerClient('tags', 'getFeaturedTags');

    const filtersToMap = isAgent || isAdmin ? FILTER_AGENT_ORDER : FILTER_GUEST_ORDER;

    const setSearchCallback = useCallback(
        search => {
            setFilters({
                ...filters,
                search
            });
        },
        [setFilters, filters]
    );

    // Use custom hook to manage island filter logic
    const setIslands = useCallback(
        islands => {
            setFilters({
                ...filters,
                islands
            });
        },
        [filters, setFilters]
    );

    const extendedIslandOptions = useIslandFilter(
        islandOptions,
        filters.islands,
        isAdmin,
        setIslands,
        true
    );

    useEffect(() => {
        const handleKeyDown = e => {
            if (e.key === 'F6') {
                e.preventDefault();
                setFilters({
                    ...filters,
                    showAsTable: !filters.showAsTable
                });
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [setFilters, filters]);

    // Validate and clean up tags if some have been removed
    useEffect(() => {
        if (!filters.tags || tagOptionsLoading) {
            return;
        }
        const newTags = filters.tags.filter(t => tagOptions.find(to => `${to.id}` === `${t}`));
        if (newTags.length === filters.tags.length) {
            return;
        }
        setFilters({
            ...filters,
            tags: newTags
        });
    }, [filters.tags, tagOptions, tagOptionsLoading, setFilters]);

    // Validate and reset sorter if invalid value was saved
    useEffect(() => {
        if (!filters.sorter || SORT_ALL_OPTIONS.find(s => s.value === filters.sorter)) {
            return;
        }
        setFilters({
            ...filters,
            sorter: undefined
        });
    }, [filters.sorter, setFilters]);

    return (
        <Grid
            item
            container
            id="golf-sidemenu"
            className="is-open"
            lg={3}
            sm={4}
            xs={12}
            direction="column"
            justifyContent="flex-start"
            alignItems="center"
            spacing={2}
            style={{ margin: 0 }}
        >
            <Grid item container className="title" justifyContent="flex-start">
                <Grid item xs={12}>
                    {vendorActivitySelected ? 'Activities' : 'Search for Activity'}
                </Grid>
                {vendorActivitySelected && (
                    <Link
                        to="#"
                        onClick={event => {
                            event.preventDefault();
                            history.push('/');
                        }}
                    >
                        <h5>Back to all activities</h5>
                    </Link>
                )}
            </Grid>
            {filtersToMap.map((field, idx) => {
                switch (field) {
                    case 'datePicker':
                        return (
                            <React.Fragment key={`datePicker-${idx}`}>
                                <FilterRangePicker
                                    filters={filters}
                                    setFilters={setFilters}
                                    label="DATE"
                                />
                                {guestTimeRange && (
                                    <TimeRangeAlert
                                        checkInDate={guestTimeRange?.checkInDate}
                                        checkOutDate={guestTimeRange?.checkOutDate}
                                        startDate={filters.checkInDate}
                                        endDate={filters.checkOutDate}
                                        setFilters={setFilters}
                                    />
                                )}
                            </React.Fragment>
                        );
                    case 'searchField':
                        return (
                            !vendorActivitySelected && (
                                <FancySearchInputField
                                    key="searchField"
                                    label="SEARCH"
                                    value={filters.search}
                                    setValue={setSearchCallback}
                                    placeholder="Search for activity or vendor"
                                />
                            )
                        );
                    case 'sort':
                        return (
                            <FilterDropdown
                                key="sort"
                                label="SORT"
                                options={SORT_ALL_OPTIONS}
                                value={filters.sorter}
                                setValue={sorter => {
                                    setFilters({ ...filters, sorter });
                                }}
                            />
                        );
                    case 'guests':
                        return (
                            <FancyPlayerCountSelect
                                key="guests"
                                label="GUESTS"
                                value={filters.guests}
                                setValue={count => {
                                    setFilters({
                                        ...filters,
                                        guests: count
                                    });
                                }}
                            />
                        );
                    case 'island':
                        return (
                            !window.clientData?.restrictedToOneIsland && (
                                <FancyButtongroupMultiSelect
                                    key="island"
                                    single
                                    label="ISLAND/REGION"
                                    options={extendedIslandOptions || []}
                                    value={filters.islands}
                                    setValue={newIslands => {
                                        setFilters({
                                            ...filters,
                                            search: '',
                                            islands: newIslands
                                        });
                                    }}
                                />
                            )
                        );
                    case 'tags':
                        return (
                            !vendorActivitySelected &&
                            !tagOptionsLoading &&
                            tagOptions?.length > 0 && (
                                <FancyButtongroupMultiSelect
                                    key="tags"
                                    label="TAGS"
                                    options={tagOptions || []}
                                    value={filters.tags}
                                    setValue={newTags => {
                                        setFilters({
                                            ...filters,
                                            search: '',
                                            tags: newTags
                                        });
                                    }}
                                />
                            )
                        );
                    case 'hideShow':
                        // the client can force the value and drop the checkbox
                        return (
                            !isTimeslotFilterLocked() && (
                                <FormControlLabel
                                    key="hideactivities"
                                    control={
                                        <Checkbox
                                            checked={!filters.showEmpty}
                                            onChange={() =>
                                                setFilters({
                                                    ...filters,
                                                    showEmpty: !filters.showEmpty
                                                })
                                            }
                                        />
                                    }
                                    label="Hide activities without timeslot"
                                />
                            )
                        );
                    case 'asTable':
                        return (
                            (isAgent || isAdmin) && (
                                <Tooltip
                                    title="Press F6 to toggle table view"
                                    placement="top"
                                    arrow
                                >
                                    <FormControlLabel
                                        key="showastable"
                                        control={
                                            <Checkbox
                                                checked={!!filters.showAsTable}
                                                onChange={() =>
                                                    setFilters({
                                                        ...filters,
                                                        showAsTable: !filters.showAsTable
                                                    })
                                                }
                                            />
                                        }
                                        label="Show as table"
                                    />
                                </Tooltip>
                            )
                        );
                    default:
                        return null;
                }
            })}
            <Grid item>
                <Button
                    variant="contained"
                    className="btn-golf-rounded wide-button"
                    color="primary"
                    disabled={notAllFiltersSelected}
                    onClick={() => {}}
                >
                    Find Activity
                </Button>
            </Grid>
            <Grid item>
                <Link
                    to="#"
                    onClick={event => {
                        event.preventDefault();
                        localStorage.removeItem('filters');
                        history.push('?');
                    }}
                >
                    <h5>Reset filters</h5>
                </Link>
            </Grid>
        </Grid>
    );
};

export default ActivityFilter;
